import { DEFAULT_AUTH_OPTIONS, getWebSocketUri } from '@flik/shared/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AsyncAPIReceiveOperation,
  AsyncAPISendOperation,
} from '../../api/__generated__/asyncapi';
import {
  TypedWebSocketMessage,
  UseWebSocketReturn,
  WebSocketConnectionState,
  WebSocketHeaders,
  WebSocketMessageData,
  WebSocketMessageHandler,
  WebSocketOperation,
} from '../types/websocket';

interface UseWebSocketOptions {
  autoConnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
}

export function useWebSocket(
  options: UseWebSocketOptions = {},
): UseWebSocketReturn {
  const {
    autoConnect = true,
    reconnectInterval = 3000,
    maxReconnectAttempts = 5,
    heartbeatInterval = 30000,
  } = options;

  const [connectionState, setConnectionState] =
    useState<WebSocketConnectionState>('disconnected');
  const [lastError, setLastError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isManualDisconnectRef = useRef(false);
  const lastTokenRef = useRef<string | null>(null);
  const messageHandlersRef = useRef<
    Map<WebSocketOperation, Set<WebSocketMessageHandler<any>>>
  >(new Map());

  const connect = useCallback(() => {
    if (
      wsRef.current?.readyState === WebSocket.CONNECTING ||
      wsRef.current?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    try {
      setConnectionState('connecting');
      setLastError(null);

      // Get the access token for authorization
      const getAccessToken = (): string | null => {
        if (DEFAULT_AUTH_OPTIONS.storage) {
          return DEFAULT_AUTH_OPTIONS.storage.get(
            DEFAULT_AUTH_OPTIONS.token.accessTokenKey,
          );
        }
        return localStorage.getItem(DEFAULT_AUTH_OPTIONS.token.accessTokenKey);
      };

      const accessToken = getAccessToken();
      let wsUrl = getWebSocketUri();

      // Use basic WebSocket URL without query parameters
      if (accessToken) {
        lastTokenRef.current = accessToken;
        console.log('WebSocket connecting with token in message headers');
      } else {
        console.warn('No access token available for WebSocket connection');
        setLastError('No access token available');
        setConnectionState('error');
        return;
      }

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnectionState('connected');
        setLastError(null);
        reconnectAttemptsRef.current = 0;
        isManualDisconnectRef.current = false;

        // Send sessionRealtimeAuth message on connection
        if (accessToken) {
          const authMessage = {
            id: `${Date.now()}-${Math.random()}`,
            operation: 'sessionRealtimeAuth',
            data: {
              accessToken,
            },
          };
          ws.send(JSON.stringify(authMessage));
          console.log('Sent sessionRealtimeAuth message');
        }

        // Start heartbeat
        startHeartbeat();
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason);
        console.log('Close event details:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          url: wsUrl,
        });
        setConnectionState('disconnected');
        stopHeartbeat();

        // Attempt to reconnect if it wasn't a manual disconnect
        if (
          !isManualDisconnectRef.current &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          setConnectionState('reconnecting');
          reconnectAttemptsRef.current++;

          console.log(
            `Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectInterval);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setLastError('Max reconnection attempts reached');
          setConnectionState('error');
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        console.log('Error details:', {
          type: error.type,
          target: error.target,
          url: wsUrl,
        });
        setLastError('WebSocket connection error');
        setConnectionState('error');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Call registered handlers for this operation
          const handlers = messageHandlersRef.current.get(message.operation);
          if (handlers) {
            handlers.forEach((handler) => {
              try {
                handler(message);
              } catch (error) {
                console.error('Error in message handler:', error);
              }
            });
          }

          // Handle pong responses
          if (message.operation === 'pong') {
            console.log('Received pong from server');
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setLastError('Failed to create WebSocket connection');
      setConnectionState('error');
    }
  }, [reconnectInterval, maxReconnectAttempts]);

  const disconnect = useCallback(() => {
    isManualDisconnectRef.current = true;
    stopHeartbeat();

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnectionState('disconnected');
  }, []);

  // Type-safe message sending function
  const sendMessage = useCallback(
    <T extends AsyncAPISendOperation>(
      operation: T,
      data: WebSocketMessageData<T>,
      headers?: WebSocketHeaders,
    ) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          // Get current access token
          const getAccessToken = (): string | null => {
            if (DEFAULT_AUTH_OPTIONS.storage) {
              return DEFAULT_AUTH_OPTIONS.storage.get(
                DEFAULT_AUTH_OPTIONS.token.accessTokenKey,
              );
            }
            return localStorage.getItem(
              DEFAULT_AUTH_OPTIONS.token.accessTokenKey,
            );
          };

          const accessToken = getAccessToken();

          // Create typed message using the proper AsyncAPI structure
          const message = {
            id: `${Date.now()}-${Math.random()}`,
            operation,
            data,
            headers: {
              ...headers,
              'X-Authorization': accessToken
                ? `Bearer ${accessToken}`
                : undefined,
            },
          } as TypedWebSocketMessage<T, WebSocketMessageData<T>>;

          wsRef.current.send(JSON.stringify(message));
        } catch (error) {
          console.error('Failed to send WebSocket message:', error);
          setLastError('Failed to send message');
        }
      } else {
        console.warn('WebSocket is not connected. Cannot send message:', {
          operation,
          data,
        });
        setLastError('WebSocket is not connected');
      }
    },
    [],
  );

  // Type-safe message handler registration
  const onMessageReceived = useCallback(
    <T extends AsyncAPIReceiveOperation>(
      operation: T,
      handler: WebSocketMessageHandler<T>,
    ) => {
      // Get or create handlers set for this operation
      if (!messageHandlersRef.current.has(operation)) {
        messageHandlersRef.current.set(operation, new Set());
      }

      const handlers = messageHandlersRef.current.get(operation)!;
      handlers.add(handler);

      // Return cleanup function
      return () => {
        handlers.delete(handler);
        // Clean up empty handler sets
        if (handlers.size === 0) {
          messageHandlersRef.current.delete(operation);
        }
      };
    },
    [],
  );

  const startHeartbeat = useCallback(() => {
    const sendHeartbeat = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        sendMessage('ping', { message: 'ping' });
      }
    };

    // Send heartbeat immediately
    sendHeartbeat();

    // Then send heartbeat at intervals
    heartbeatTimeoutRef.current = setInterval(sendHeartbeat, heartbeatInterval);
  }, [sendMessage, heartbeatInterval]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearInterval(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    isManualDisconnectRef.current = false;
    connect();
  }, [disconnect, connect]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // Monitor token changes and reconnect if needed
  useEffect(() => {
    if (!autoConnect) return;

    const checkTokenChange = () => {
      const getAccessToken = (): string | null => {
        if (DEFAULT_AUTH_OPTIONS.storage) {
          return DEFAULT_AUTH_OPTIONS.storage.get(
            DEFAULT_AUTH_OPTIONS.token.accessTokenKey,
          );
        }
        return localStorage.getItem(DEFAULT_AUTH_OPTIONS.token.accessTokenKey);
      };

      const currentToken = getAccessToken();

      // If token changed and we're connected, reconnect
      if (
        currentToken !== lastTokenRef.current &&
        wsRef.current?.readyState === WebSocket.OPEN
      ) {
        console.log('Token changed, reconnecting WebSocket...');
        disconnect();
        setTimeout(() => connect(), 100);
      }
    };

    // Check for token changes every 5 seconds
    const tokenCheckInterval = setInterval(checkTokenChange, 5000);

    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, [autoConnect, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all message handlers
      messageHandlersRef.current.clear();
      disconnect();
    };
  }, [disconnect]);

  return {
    connectionState,
    sendMessage,
    onMessageReceived,
    isConnected: connectionState === 'connected',
    lastError,
    reconnect,
  };
}
